import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const RecordPaymentModal = ({ language, invoice, onClose, onSubmit }) => {
  const [paymentData, setPaymentData] = useState({
    amount: invoice ? invoice?.amount - (invoice?.paidAmount || 0) : 0,
    date: new Date()?.toISOString()?.split('T')?.[0],
    method: 'bank_transfer',
    reference: ''
  });

  const paymentMethodOptions = [
    { 
      value: 'bank_transfer', 
      label: language === 'fr' ? 'Virement bancaire' : language === 'ar' ? 'تحويل بنكي' : 'Bank Transfer' 
    },
    { 
      value: 'cash', 
      label: language === 'fr' ? 'Espèces' : language === 'ar' ? 'نقدًا' : 'Cash' 
    },
    { 
      value: 'check', 
      label: language === 'fr' ? 'Chèque' : language === 'ar' ? 'شيك' : 'Check' 
    },
    { 
      value: 'credit_card', 
      label: language === 'fr' ? 'Carte de crédit' : language === 'ar' ? 'بطاقة ائتمان' : 'Credit Card' 
    }
  ];

  const labels = {
    title: language === 'fr' ? 'Enregistrer un paiement' : language === 'ar' ? 'تسجيل دفعة' : 'Record Payment',
    invoiceNumber: language === 'fr' ? 'N° Facture' : language === 'ar' ? 'رقم الفاتورة' : 'Invoice Number',
    client: language === 'fr' ? 'Client' : language === 'ar' ? 'العميل' : 'Client',
    totalAmount: language === 'fr' ? 'Montant total' : language === 'ar' ? 'المبلغ الإجمالي' : 'Total Amount',
    paidAmount: language === 'fr' ? 'Déjà payé' : language === 'ar' ? 'المدفوع بالفعل' : 'Already Paid',
    balanceDue: language === 'fr' ? 'Solde restant' : language === 'ar' ? 'الرصيد المتبقي' : 'Balance Due',
    paymentAmount: language === 'fr' ? 'Montant du paiement (MAD)' : language === 'ar' ? 'مبلغ الدفع (درهم)' : 'Payment Amount (MAD)',
    paymentDate: language === 'fr' ? 'Date du paiement' : language === 'ar' ? 'تاريخ الدفع' : 'Payment Date',
    paymentMethod: language === 'fr' ? 'Méthode de paiement' : language === 'ar' ? 'طريقة الدفع' : 'Payment Method',
    reference: language === 'fr' ? 'Référence / N° de transaction' : language === 'ar' ? 'المرجع / رقم المعاملة' : 'Reference / Transaction Number',
    cancel: language === 'fr' ? 'Annuler' : language === 'ar' ? 'إلغاء' : 'Cancel',
    record: language === 'fr' ? 'Enregistrer le paiement' : language === 'ar' ? 'تسجيل الدفع' : 'Record Payment'
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat(language === 'ar' ? 'ar-MA' : 'fr-MA', {
      style: 'currency',
      currency: 'MAD'
    })?.format(amount);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSubmit({ ...paymentData, invoiceId: invoice?.id });
  };

  if (!invoice) return null;

  const balanceDue = invoice?.amount - (invoice?.paidAmount || 0);

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[150] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-border">
          <h2 className="text-lg md:text-xl font-semibold text-foreground">
            {labels?.title}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
            aria-label="Close"
          />
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4 md:p-6">
          {/* Invoice Summary */}
          <div className="bg-muted rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-muted-foreground block mb-1">
                  {labels?.invoiceNumber}
                </span>
                <span className="text-sm font-medium text-foreground monospace">
                  {invoice?.invoiceNumber}
                </span>
              </div>
              <div>
                <span className="text-sm text-muted-foreground block mb-1">
                  {labels?.client}
                </span>
                <span className="text-sm font-medium text-foreground">
                  {invoice?.clientName}
                </span>
              </div>
              <div>
                <span className="text-sm text-muted-foreground block mb-1">
                  {labels?.totalAmount}
                </span>
                <span className="text-sm font-medium text-foreground monospace">
                  {formatAmount(invoice?.amount)}
                </span>
              </div>
              <div>
                <span className="text-sm text-muted-foreground block mb-1">
                  {labels?.paidAmount}
                </span>
                <span className="text-sm font-medium text-success monospace">
                  {formatAmount(invoice?.paidAmount || 0)}
                </span>
              </div>
              <div className="md:col-span-2">
                <span className="text-sm text-muted-foreground block mb-1">
                  {labels?.balanceDue}
                </span>
                <span className="text-lg font-bold text-error monospace">
                  {formatAmount(balanceDue)}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          <div className="space-y-4">
            <Input
              label={labels?.paymentAmount}
              type="number"
              min="0"
              max={balanceDue}
              step="0.01"
              value={paymentData?.amount}
              onChange={(e) => setPaymentData({ ...paymentData, amount: parseFloat(e?.target?.value) })}
              required
            />

            <Input
              label={labels?.paymentDate}
              type="date"
              value={paymentData?.date}
              onChange={(e) => setPaymentData({ ...paymentData, date: e?.target?.value })}
              required
            />

            <Select
              label={labels?.paymentMethod}
              options={paymentMethodOptions}
              value={paymentData?.method}
              onChange={(value) => setPaymentData({ ...paymentData, method: value })}
              required
            />

            <Input
              label={labels?.reference}
              type="text"
              placeholder="TRX-2024-001"
              value={paymentData?.reference}
              onChange={(e) => setPaymentData({ ...paymentData, reference: e?.target?.value })}
              required
            />
          </div>

          {/* Actions */}
          <div className="flex flex-col-reverse md:flex-row gap-3 mt-6 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              fullWidth
            >
              {labels?.cancel}
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="CreditCard"
              iconPosition="left"
              fullWidth
            >
              {labels?.record}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecordPaymentModal;